// LiveDataContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import { buildWsUrl } from "./wsUrl";
import { parsePredictionMessage } from "../main_/components/predictionFormat";

function forecastSocketKey(user) {
  if (!user || user.id == null) return "signed-out";
  const level = user.subscription && user.subscription.subscription_level;
  return `${user.id}:${level || ""}`;
}

async function fetchPredictionsTicket() {
  const response = await fetch("/api/predictions/ws-ticket/", { credentials: "include" });
  if (!response.ok) return null;
  const body = await response.json();
  return body && typeof body.ticket === "string" ? body.ticket : null;
}

// Maximum number of data points to store per subscription
const MAX_DATA_POINTS = 1000;
// Ping interval in milliseconds
const PING_INTERVAL = 10000;

const LiveDataContext = createContext();

export const LiveDataProvider = ({ children }) => {
  const authState = useContext(AuthContext);
  const authLoading = Boolean(authState && authState.loading);
  const forecastKey = !authState || authState.loading ? null : forecastSocketKey(authState.user);
  const forecastKeyRef = useRef(null);
  const pendingPredReconnectRef = useRef(false);
  /* ----------------------
     Existing live socket refs
     ---------------------- */
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const wsRef = useRef(null);
  // subscriptions: key = "measurement:field" → { callbacks: Set(), data: { x: [], y: [] } }
  const subscriptionsRef = useRef({});

  // Reconnection parameters for live socket
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const MAX_BACKOFF = 30000; // maximum delay 30 seconds

  /* ----------------------
     New predictions socket refs (separate socket)
     ---------------------- */
  const [predConnectionStatus, setPredConnectionStatus] = useState("disconnected");
  const predWsRef = useRef(null);
  // predictionsSubscriptions: key = "measurement:field" → { callbacks: Set(), latest: any }
  const predSubscriptionsRef = useRef({});
  const predReconnectAttempts = useRef(0);
  const predReconnectTimeout = useRef(null);
  const predPingInterval = useRef(null);
  const predManualCloseRef = useRef(false);
  const predServerSubsRef = useRef(new Set());
  const predAuthedRef = useRef(false);

  // Child subscriptions read `latest` in their own effects, which run before this
  // provider's effects. Drop the previous account's payload before those effects.
  if (forecastKey != null && forecastKeyRef.current !== forecastKey) {
    if (forecastKeyRef.current !== null) {
      Object.keys(predSubscriptionsRef.current).forEach((key) => {
        const entry = predSubscriptionsRef.current[key];
        if (entry) entry.latest = null;
      });
      pendingPredReconnectRef.current = true;
    }
    forecastKeyRef.current = forecastKey;
  }

  /* ----------------------
     Helper: build WS URL (adjust host if needed)
     ---------------------- */
  const LIVE_WS_URL = "wss://aviatortrends.com/ws/live/";
  const PRED_WS_URL = buildWsUrl("/ws/predictions/");

  /* ======================
     --- LIVE SOCKET ---
     ====================== */

  const connect = () => {
    // Clear any existing ping interval
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
    }

    setConnectionStatus("connecting");

    const ws = new WebSocket(LIVE_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[LiveData] WebSocket connected.");
      setConnectionStatus("connected");

      // Reset reconnection attempts on successful connection.
      reconnectAttempts.current = 0;

      // Set up ping interval
      pingInterval.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "pong" }));
        }
      }, PING_INTERVAL);

      // Re-subscribe to all active subscriptions
      Object.keys(subscriptionsRef.current).forEach((key) => {
        const [measurement, field] = key.split(":");
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: "subscribe", measurement, field }));
        }
      });
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        // Handle ping messages
        if (message.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }

        // Handle error messages
        if (message.error) {
          console.error("[LiveData] Server error:", message.error);
          return;
        }

        // Expected message format: { measurement, field, times, values, truncated? }
        const { measurement, field, times, values, truncated } = message;
        if (!measurement || !field || !times || !values) {
          return; // Skip invalid messages
        }

        const key = `${measurement}:${field}`;

        // Initialize subscription if not already present
        if (!subscriptionsRef.current[key]) {
          subscriptionsRef.current[key] = { callbacks: new Set(), data: { x: [], y: [] } };
        }

        const subscription = subscriptionsRef.current[key];

        // If server indicates truncated data or we need to limit locally
        if (truncated) {
          // Replace all data with what server sent (already truncated)
          subscription.data = { x: times, y: values };
        } else {
          // Append new data
          subscription.data.x = subscription.data.x.concat(times);
          subscription.data.y = subscription.data.y.concat(values);

          // Truncate if exceeding maximum data points
          if (subscription.data.x.length > MAX_DATA_POINTS) {
            const excess = subscription.data.x.length - MAX_DATA_POINTS;
            subscription.data.x = subscription.data.x.slice(excess);
            subscription.data.y = subscription.data.y.slice(excess);
          }
        }

        // Notify all subscribers
        subscription.callbacks.forEach((cb) =>
          cb({ x: subscription.data.x, y: subscription.data.y })
        );
      } catch (error) {
        console.error("[LiveData] Error processing message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("[LiveData] WebSocket error:", error);
      setConnectionStatus("error");
      // Close the socket so that onclose triggers reconnection
      ws.close();
    };

    ws.onclose = () => {
      console.warn("[LiveData] WebSocket disconnected.");
      setConnectionStatus("disconnected");

      // Clear ping interval
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
        pingInterval.current = null;
      }

      attemptReconnect();
    };
  };

  // Attempt reconnection with exponential backoff (live socket)
  const attemptReconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    const delay = Math.min(1000 * 2 ** reconnectAttempts.current, MAX_BACKOFF);
    console.log(`[LiveData] Attempting reconnection in ${delay}ms...`);
    setConnectionStatus("reconnecting");

    reconnectTimeout.current = setTimeout(() => {
      reconnectAttempts.current += 1;
      connect();
    }, delay);
  };

  // Cleanup function that properly closes resources (live)
  const cleanup = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }

    // Clear all subscriptions
    Object.keys(subscriptionsRef.current).forEach((key) => {
      subscriptionsRef.current[key].callbacks.clear();
    });
    subscriptionsRef.current = {};
  };

  /* ======================
     --- PREDICTIONS SOCKET ---
     ====================== */

  const detachPredSocket = (ws) => {
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (predWsRef.current === ws) predWsRef.current = null;
    try {
      ws.close();
    } catch (err) {
      console.error("[Predictions] websocket error", err);
    }
  };

  const sendPredSubscriptions = (ws) => {
    Object.keys(predSubscriptionsRef.current).forEach((key) => {
      const sub = predSubscriptionsRef.current[key];
      if (!sub || sub.callbacks.size === 0 || predServerSubsRef.current.has(key)) return;
      const splitAt = key.indexOf(":");
      const measurement = key.slice(0, splitAt);
      const field = key.slice(splitAt + 1);
      if (ws.readyState === WebSocket.OPEN) {
        console.log("[Predictions] subscribe", { measurement, field });
        ws.send(JSON.stringify({ action: "subscribe", measurement, field }));
        predServerSubsRef.current.add(key);
      }
    });
  };

  const connectPredictions = () => {
    // A new socket is not the intentional shutdown from the previous effect cleanup.
    predManualCloseRef.current = false;
    predAuthedRef.current = false;
    detachPredSocket(predWsRef.current);

    if (predPingInterval.current) {
      clearInterval(predPingInterval.current);
    }

    console.log("[Predictions] socket URL", PRED_WS_URL);
    setPredConnectionStatus("connecting");
    const ws = new WebSocket(PRED_WS_URL);
    predWsRef.current = ws;

    ws.onopen = () => {
      if (predWsRef.current !== ws) return;
      console.log("[Predictions] socket open", PRED_WS_URL);
      setPredConnectionStatus("connected");

      predReconnectAttempts.current = 0;
      predServerSubsRef.current = new Set();
      predAuthedRef.current = false;

      predPingInterval.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "pong" }));
        }
      }, PING_INTERVAL);

      // Authenticate before any subscribe. A missing ticket stays anonymous.
      return (async () => {
        let ticket = null;
        try {
          ticket = await fetchPredictionsTicket();
        } catch (err) {
          ticket = null;
        }
        if (predWsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({ type: "authenticate", ticket }));
      })();
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        if (message.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (message.type === "predictions_auth") {
          if (predWsRef.current !== ws) return;
          predAuthedRef.current = true;
          sendPredSubscriptions(ws);
          return;
        }
        if (message.type === "pong" || message.error || message.status === "unsubscribed") {
          return;
        }

        const parsed = parsePredictionMessage(message);
        if (!parsed) return;
        const { measurement, field, payload } = parsed;

        const key = `${measurement}:${field}`;
        if (!predSubscriptionsRef.current[key]) {
          // keep a minimal entry so later subscribe can get latest via getLatestPredictions
          predSubscriptionsRef.current[key] = { callbacks: new Set(), latest: payload };
        } else {
          predSubscriptionsRef.current[key].latest = payload;
        }

        // Call callbacks (if any) with the raw payload object
        const sub = predSubscriptionsRef.current[key];
        if (sub && sub.callbacks) {
          sub.callbacks.forEach((cb) => {
            try {
              cb({ measurement, field, payload });
            } catch (ex) {
              console.error("[Predictions] callback error:", ex);
            }
          });
        }
      } catch (err) {
        console.error("[Predictions] Error parsing message:", err);
      }
    };

    ws.onerror = () => {
      if (predWsRef.current !== ws) return;
      console.error("[Predictions] websocket error", PRED_WS_URL);
      setPredConnectionStatus("error");
    };

    ws.onclose = (event) => {
      const code = event && event.code;
      const reason = event && event.reason;
      if (predWsRef.current !== ws) {
        console.warn("[Predictions] socket close ignored", code, reason);
        return;
      }
      console.warn("[Predictions] socket close", code, reason);
      predWsRef.current = null;
      predAuthedRef.current = false;
      setPredConnectionStatus("disconnected");
      predServerSubsRef.current = new Set();

      if (predPingInterval.current) {
        clearInterval(predPingInterval.current);
        predPingInterval.current = null;
      }

      if (predManualCloseRef.current) {
        predManualCloseRef.current = false;
        return;
      }
      attemptPredReconnect();
    };
  };

  const attemptPredReconnect = () => {
    if (predReconnectTimeout.current) {
      clearTimeout(predReconnectTimeout.current);
    }

    const delay = Math.min(1000 * 2 ** predReconnectAttempts.current, MAX_BACKOFF);
    console.log(`[Predictions] Attempting reconnection in ${delay}ms...`);
    setPredConnectionStatus("reconnecting");

    predReconnectTimeout.current = setTimeout(() => {
      predReconnectAttempts.current += 1;
      connectPredictions();
    }, delay);
  };

  const cleanupPredictions = () => {
    predManualCloseRef.current = true;
    predServerSubsRef.current = new Set();
    detachPredSocket(predWsRef.current);

    if (predReconnectTimeout.current) {
      clearTimeout(predReconnectTimeout.current);
      predReconnectTimeout.current = null;
    }

    if (predPingInterval.current) {
      clearInterval(predPingInterval.current);
      predPingInterval.current = null;
    }

    // keep last known payloads but clear callbacks
    Object.keys(predSubscriptionsRef.current).forEach((key) => {
      predSubscriptionsRef.current[key].callbacks.clear();
    });
    // Keep the latest payload available in predSubscriptionsRef[key].latest
  };

  /* ======================
     React lifecycle: start both sockets
     ====================== */
  useEffect(() => {
    connect();
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Wait until the signed-in user is known so the first socket can fetch its ticket.
    if (authLoading) return undefined;
    connectPredictions();
    return () => {
      cleanupPredictions();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  /* ======================
     Public API for Live data (UNCHANGED)
     ====================== */

  const subscribe = ({ measurement, field, callback }) => {
    const key = `${measurement}:${field}`;

    if (!subscriptionsRef.current[key]) {
      subscriptionsRef.current[key] = { callbacks: new Set(), data: { x: [], y: [] } };
      // If the socket is open, send a subscribe message
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: "subscribe", measurement, field }));
      }
    }

    subscriptionsRef.current[key].callbacks.add(callback);

    // Return an unsubscribe function for easy cleanup
    return () => unsubscribe({ measurement, field, callback });
  };

  const unsubscribe = ({ measurement, field, callback }) => {
    const key = `${measurement}:${field}`;

    if (subscriptionsRef.current[key]) {
      subscriptionsRef.current[key].callbacks.delete(callback);

      // If there are no more subscribers, unsubscribe from the server
      if (subscriptionsRef.current[key].callbacks.size === 0) {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: "unsubscribe", measurement, field }));
        }

        // Clean up subscription data
        delete subscriptionsRef.current[key];
      }
    }
  };

  const getLatestData = ({ measurement, field }) => {
    const key = `${measurement}:${field}`;
    return subscriptionsRef.current[key]?.data || { x: [], y: [] };
  };

  const reconnect = () => {
    cleanup();
    reconnectAttempts.current = 0;
    connect();
  };

  /* ======================
     Public API for Predictions (NEW)
     ====================== */

  const subscribePredictions = ({ measurement, field, callback }) => {
    const key = `${measurement}:${field}`;

    if (!predSubscriptionsRef.current[key]) {
      predSubscriptionsRef.current[key] = { callbacks: new Set(), latest: null };
    }
    if (
      predAuthedRef.current &&
      predWsRef.current &&
      predWsRef.current.readyState === WebSocket.OPEN &&
      !predServerSubsRef.current.has(key)
    ) {
      console.log("[Predictions] subscribe", { measurement, field });
      predWsRef.current.send(JSON.stringify({ action: "subscribe", measurement, field }));
      predServerSubsRef.current.add(key);
    }

    predSubscriptionsRef.current[key].callbacks.add(callback);

    // If we already have a latest payload, call the callback immediately with it
    const latest = predSubscriptionsRef.current[key].latest;
    if (latest !== null && latest !== undefined) {
      try {
        callback({ measurement, field, payload: latest });
      } catch (ex) {
        // swallow callback errors
        console.error("[Predictions] immediate callback error:", ex);
      }
    }

    return () => unsubscribePredictions({ measurement, field, callback });
  };

  const unsubscribePredictions = ({ measurement, field, callback }) => {
    const key = `${measurement}:${field}`;

    if (predSubscriptionsRef.current[key]) {
      predSubscriptionsRef.current[key].callbacks.delete(callback);

      // If no callbacks, unsubscribe server-side and keep latest payload cached
      if (predSubscriptionsRef.current[key].callbacks.size === 0) {
        if (predWsRef.current && predWsRef.current.readyState === WebSocket.OPEN) {
          predWsRef.current.send(JSON.stringify({ action: "unsubscribe", measurement, field }));
        }
        predServerSubsRef.current.delete(key);
        predSubscriptionsRef.current[key].callbacks.clear();
      }
    }
  };

  const getLatestPredictions = ({ measurement, field }) => {
    const key = `${measurement}:${field}`;
    return predSubscriptionsRef.current[key]?.latest ?? null;
  };

  const reconnectPredictions = () => {
    predManualCloseRef.current = true;
    detachPredSocket(predWsRef.current);
    if (predReconnectTimeout.current) {
      clearTimeout(predReconnectTimeout.current);
      predReconnectTimeout.current = null;
    }
    if (predPingInterval.current) {
      clearInterval(predPingInterval.current);
      predPingInterval.current = null;
    }
    predReconnectAttempts.current = 0;
    predManualCloseRef.current = false;
    connectPredictions();
  };

  const apiRef = useRef({});
  apiRef.current = {
    subscribe,
    unsubscribe,
    getLatestData,
    reconnect,
    subscribePredictions,
    unsubscribePredictions,
    getLatestPredictions,
    reconnectPredictions,
  };
  const stableSubscribe = useCallback((args) => apiRef.current.subscribe(args), []);
  const stableUnsubscribe = useCallback((args) => apiRef.current.unsubscribe(args), []);
  const stableGetLatestData = useCallback((args) => apiRef.current.getLatestData(args), []);
  const stableReconnect = useCallback(() => apiRef.current.reconnect(), []);
  const stableSubscribePredictions = useCallback((args) => apiRef.current.subscribePredictions(args), []);
  const stableUnsubscribePredictions = useCallback((args) => apiRef.current.unsubscribePredictions(args), []);
  const stableGetLatestPredictions = useCallback((args) => apiRef.current.getLatestPredictions(args), []);
  const stableReconnectPredictions = useCallback(() => apiRef.current.reconnectPredictions(), []);

  useEffect(() => {
    if (!pendingPredReconnectRef.current) return;
    pendingPredReconnectRef.current = false;
    stableReconnectPredictions();
  }, [forecastKey, stableReconnectPredictions]);

  const value = useMemo(() => ({
    subscribe: stableSubscribe,
    unsubscribe: stableUnsubscribe,
    getLatestData: stableGetLatestData,
    reconnect: stableReconnect,
    connectionStatus,
    subscribePredictions: stableSubscribePredictions,
    unsubscribePredictions: stableUnsubscribePredictions,
    getLatestPredictions: stableGetLatestPredictions,
    reconnectPredictions: stableReconnectPredictions,
    predConnectionStatus,
  }), [
    connectionStatus,
    predConnectionStatus,
    stableSubscribe,
    stableUnsubscribe,
    stableGetLatestData,
    stableReconnect,
    stableSubscribePredictions,
    stableUnsubscribePredictions,
    stableGetLatestPredictions,
    stableReconnectPredictions,
  ]);

  /* ======================
     Provider value
     ====================== */
  return (
    <LiveDataContext.Provider value={value}>
      {children}
    </LiveDataContext.Provider>
  );
};

export const useLiveData = () => useContext(LiveDataContext);
